import {
  CampoDeFiltro,
  CampoDeMoeda,
  GrupoDeFiltro,
  OpcaoDeFiltro,
  type FiltroMarcado,
} from '../../../componentes/ui';
import { MODALIDADES, type Modalidade } from '../../../servicos/prospeccao';

/** O que está marcado no "Filtro" da Prospecção. */
export interface EscolhasDoFiltro {
  readonly novas: boolean;
  readonly foraDoCrm: boolean;
  readonly modalidades: readonly Modalidade[];
  /** "Créditos acima de:" marcado — o valor digitado fica em `valor`. */
  readonly acimaDe: boolean;
  /** Como digitado no campo de moeda ("1.000.000,00"). */
  readonly valor: string;
}

export const FILTRO_VAZIO: EscolhasDoFiltro = {
  novas: false,
  foraDoCrm: false,
  modalidades: [],
  acimaDe: false,
  valor: '',
};

/**
 * O "Filtro" da Prospecção: situação (novas no mês, fora do CRM), tipo de
 * crédito (Exportação, Diferimento, Investimento) e "Créditos acima de:", que
 * abre o campo do valor quando marcado.
 *
 * Situação e valor valem juntos; os tipos de crédito somam entre si — a regra
 * é da API (`listarEmpresasDaColetaSchema`), e o painel diz isso nos grupos.
 * O campo e o painel são os do kit (`CampoDeFiltro`).
 */
export function FiltroDaProspeccao(props: {
  readonly escolhas: EscolhasDoFiltro;
  readonly aoMudar: (escolhas: EscolhasDoFiltro) => void;
  readonly className?: string;
}): JSX.Element {
  const { escolhas, aoMudar } = props;

  const mudar = (parte: Partial<EscolhasDoFiltro>): void => aoMudar({ ...escolhas, ...parte });
  const alternarModalidade = (m: Modalidade): void =>
    mudar({
      // Na ordem da lista, sempre — a chip e a URL não dependem da ordem do clique.
      modalidades: escolhas.modalidades.includes(m)
        ? escolhas.modalidades.filter((x) => x !== m)
        : MODALIDADES.map((x) => x.chave).filter(
            (x) => x === m || escolhas.modalidades.includes(x),
          ),
    });

  const marcados: FiltroMarcado[] = [
    ...(escolhas.novas ? [{ rotulo: 'Novas no mês', tirar: () => mudar({ novas: false }) }] : []),
    ...(escolhas.foraDoCrm
      ? [{ rotulo: 'Fora do CRM', tirar: () => mudar({ foraDoCrm: false }) }]
      : []),
    ...MODALIDADES.filter((m) => escolhas.modalidades.includes(m.chave)).map((m) => ({
      rotulo: m.rotulo,
      tirar: () => alternarModalidade(m.chave),
    })),
    ...(escolhas.acimaDe
      ? [
          {
            rotulo: escolhas.valor === '' ? 'Acima de…' : `Acima de R$ ${escolhas.valor}`,
            tirar: () => mudar({ acimaDe: false, valor: '' }),
          },
        ]
      : []),
  ];

  return (
    <CampoDeFiltro
      marcados={marcados}
      semFiltro="Todas as empresas"
      aoLimpar={() => aoMudar(FILTRO_VAZIO)}
      {...(props.className === undefined ? {} : { className: props.className })}
    >
      <GrupoDeFiltro titulo="Situação">
        <OpcaoDeFiltro
          rotulo="Novas no mês"
          marcada={escolhas.novas}
          aoMudar={(v) => mudar({ novas: v })}
        />
        <OpcaoDeFiltro
          rotulo="Fora do CRM"
          marcada={escolhas.foraDoCrm}
          aoMudar={(v) => mudar({ foraDoCrm: v })}
        />
      </GrupoDeFiltro>

      <GrupoDeFiltro
        titulo="Tipo de crédito"
        nota="Marcando mais de um, entra quem tem qualquer um deles."
      >
        {MODALIDADES.map((m) => (
          <OpcaoDeFiltro
            key={m.chave}
            rotulo={m.rotulo}
            marcada={escolhas.modalidades.includes(m.chave)}
            aoMudar={() => alternarModalidade(m.chave)}
          />
        ))}
      </GrupoDeFiltro>

      <GrupoDeFiltro titulo="Valor">
        <OpcaoDeFiltro
          rotulo="Créditos acima de:"
          marcada={escolhas.acimaDe}
          aoMudar={(v) => mudar({ acimaDe: v, ...(v ? {} : { valor: '' }) })}
        />
        {escolhas.acimaDe && (
          <div className="flex flex-col gap-1 pl-7">
            <CampoDeMoeda
              rotulo="Créditos acima de"
              rotuloOculto
              autoFocus
              value={escolhas.valor}
              onChange={(valor) => mudar({ valor })}
            />
            <p className="text-xs text-conteudo-sutil">
              {escolhas.modalidades.length > 0
                ? 'Vale para a soma dos tipos de crédito marcados.'
                : 'Vale para o crédito total da empresa.'}
            </p>
          </div>
        )}
      </GrupoDeFiltro>
    </CampoDeFiltro>
  );
}
